import { Button, Input, Space, Table, Tag } from "antd";
import { EditOutlined } from "@ant-design/icons";
import dayjs from "dayjs";
import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { loadAllStaff } from "../../redux/rtk/features/user/userSlice";
import ColVisibilityDropdown from "../Shared/ColVisibilityDropdown";
import ViewBtn from "../Buttons/ViewBtn";
import BtnViewSvg from "../UI/Button/btnViewSvg";

function GetAllPromotionCycles({ data = [], onEdit, onRefresh }) {
	const dispatch = useDispatch();
	const [columnsToShow, setColumnsToShow] = useState([]);
	const [searchCycle, setSearchCycle] = useState("");
	const [filterStatus, setFilterStatus] = useState("");
	const [filterType, setFilterType] = useState("");
	const staffList = useSelector((state) => state.users?.list || []);

	useEffect(() => {
		dispatch(loadAllStaff({ status: true }));
	}, [dispatch]);

	const getStatusColor = (status) => {
		switch (status) {
			case "Active":
				return "green";
			case "Planning":
				return "blue";
			case "Completed":
				return "purple";
			case "Cancelled":
				return "red";
			default:
				return "default";
		}
	};

	const getTypeColor = (type) => {
		switch (type) {
			case "Annual":
				return "purple";
			case "Semi-Annual":
				return "blue";
			case "Quarterly":
				return "cyan";
			case "Ad-hoc":
				return "orange";
			default:
				return "default";
		}
	};

	const columns = [
		{
			id: 1,
			title: "S.No",
			dataIndex: "serial",
			key: "serial",
		},
		{
			title: "Cycle Name",
			dataIndex: "cycleName",
			key: "cycleName",
		},
		{
			title: "Cycle Type",
			dataIndex: "cycleType",
			key: "cycleType",
			render: (type) => (
				<Tag color={getTypeColor(type)}>
					{type}
				</Tag>
			),
		},
		{
			title: "Start Date",
			dataIndex: "startDate",
			key: "startDate",
			render: (date) => (date ? dayjs(date).format("DD-MM-YYYY") : "-"),
		},
		{
			title: "End Date",
			dataIndex: "endDate",
			key: "endDate",
			render: (date) => (date ? dayjs(date).format("DD-MM-YYYY") : "-"),
		},
		{
			title: "Status",
			dataIndex: "status",
			key: "status",
			render: (status) => (
				<Tag color={getStatusColor(status)}>
					{status}
				</Tag>
			),
		},
		{
			title: "Budget (₹)",
			dataIndex: "budgetAllocation",
			key: "budgetAllocation",
			render: (amount) =>
				amount ? `₹${parseFloat(amount).toLocaleString("en-IN")}` : "-",
		},
		{
			title: "Eligible Employees",
			dataIndex: "eligibleEmployees",
			key: "eligibleEmployees",
			align: "center",
		},
		{
			title: "Announcement Date",
			dataIndex: "announcementDate",
			key: "announcementDate",
			render: (date) => (date ? dayjs(date).format("DD-MM-YYYY") : "-"),
		},
		{
			title: "Action",
			key: "action",
			render: (_, record) => (
				<Space size="middle">
					<ViewBtn
						path={`/admin/promotionCycle/${record.id}`}
						onClick={() => {
							localStorage.setItem("selectedPromotionCycle", JSON.stringify(record));
						}}
					/>
					<Button
						type="primary"
						icon={<EditOutlined />}
						size="small"
						onClick={() => onEdit && onEdit(record)}
					>
						Edit
					</Button>
				</Space>
			),
		},
	];

	const filteredData = data
		.map((item, index) => ({
			...item,
			serial: index + 1,
		}))
		.filter((item) => {
			const matchesSearch =
				!searchCycle ||
				item.cycleName?.toLowerCase().includes(searchCycle.toLowerCase());
			const matchesStatus = !filterStatus || item.status === filterStatus;
			const matchesType = !filterType || item.cycleType === filterType;
			return matchesSearch && matchesStatus && matchesType;
		});

	const uniqueStatuses = [...new Set(data.map((item) => item.status))].filter(Boolean);
	const uniqueTypes = [...new Set(data.map((item) => item.cycleType))].filter(Boolean);

	return (
		<div className="card-custom">
			<div className="d-flex justify-content-between align-items-center mb-3">
				<h4 className="mb-0">Promotion Cycles List</h4>
				<Space>
					<Input
						placeholder="Search by cycle name"
						value={searchCycle}
						onChange={(e) => setSearchCycle(e.target.value)}
						style={{ width: 200 }}
						allowClear
					/>
					<ColVisibilityDropdown
						options={columns.map((col) => ({
							key: col.key,
							label: col.title,
						}))}
						columnsToShow={columnsToShow}
						setColumnsToShow={setColumnsToShow}
					/>
				</Space>
			</div>

			<Space className="mb-3">
				<Input
					placeholder="Filter by status"
					value={filterStatus}
					onChange={(e) => setFilterStatus(e.target.value)}
					style={{ width: 150 }}
					allowClear
				/>
				<Input
					placeholder="Filter by type"
					value={filterType}
					onChange={(e) => setFilterType(e.target.value)}
					style={{ width: 150 }}
					allowClear
				/>
			</Space>

			<Table
				columns={columns.filter((col) =>
					columnsToShow.length === 0
						? true
						: columnsToShow.includes(col.key)
				)}
				dataSource={filteredData}
				rowKey="id"
				scroll={{ x: true }}
				pagination={{
					pageSize: 10,
					showSizeChanger: true,
					showTotal: (total, range) =>
						`${range[0]}-${range[1]} of ${total} items`,
				}}
			/>
		</div>
	);
}

export default GetAllPromotionCycles;

